import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const MyanmarPage = () => {
  const myanmarData = {
    name: 'Myanmar',
    region: 'Southeast Asia',
    color: '#7090A0',
    metaTitle: 'Pharma & Nutraceutical Registration in Myanmar | FDA Myanmar | ACTD Dossiers',
    metaDescription: 'Understand how to register pharmaceutical and nutraceutical products in Myanmar. FDA Myanmar requirements, ASEAN CTD (ACTD) dossier format, supplement rules, documents and market opportunity in a largely import-dependent Southeast Asian market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Myanmar',
      subtitle: 'An import-dependent, price-sensitive market regulated by FDA Myanmar, using ASEAN CTD (ACTD) dossiers and balancing infectious disease with a fast-growing NCD burden.',
      cta: 'Start Your Myanmar Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~54M' },
      { label: 'Income Level', value: 'Lower-middle income' },
      { label: 'Health Expenditure per Capita', value: '~USD 63' },
      { label: 'Pharma Imports (2024)', value: '~USD 341M' },
      { label: 'Import Reliance', value: '~80% imported' },
      { label: 'Top Supplier', value: 'India (major role)' }
    ],
    
    marketOverview: {
      title: 'Market Overview: High Import Dependence, Competitive Generics',
      stats: [
        'Pharmaceutical market underdeveloped locally - most supplies imported',
        '~80% of pharmaceutical goods imported (Rx, OTC, nutraceuticals)',
        'Pharmaceutical imports ~USD 340.9 million in 2024',
        'India is top medicine supplier (USD 200-300M+ from India alone)',
        'Public sector: Ministry of Health facilities, donor-funded programs',
        'Private sector: Clinics, hospitals, large pharmacy network (Yangon, Mandalay)',
        'Strong preference for affordable generics with acceptable documentation',
        'Rising interest in vitamins, blood builders, liver tonics, herbal combinations, immunity boosters'
      ],
      opportunities: [
        'Limited local pharma manufacturing - room for well-documented Indian products',
        'Food Control Division of FDA regulates OTC and food supplements',
        'Price-sensitive market favoring quality generics',
        'ACTD-based system allows adaptation to other ASEAN markets'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: Dual Burden of Infection and NCDs',
      subtitle: 'Myanmar faces both significant infectious disease challenges and a growing NCD burden, creating diverse pharmaceutical and nutraceutical opportunities.',
      categories: [
        {
          name: 'Non-Communicable Diseases — Growing Burden',
          items: [
            'About 23% of adults have raised blood pressure',
            'Around 6-7% have raised blood glucose (likely underdiagnosed)',
            'Adult diabetes: 5.7% nationally, 11-12% in urban Yangon',
            'Hypertension prevalence: 27.8% to 38.6% depending on region/age',
            'Risk factors: tobacco use, unhealthy diet, physical inactivity, high salt',
            'Pharma: Antihypertensives, antidiabetics, lipid-lowering, cardiology drugs',
            'Nutra: Metabolic health, cardio-protective, antioxidant, weight-management supplements'
          ]
        },
        {
          name: 'Infectious Diseases & Nutrition',
          items: [
            'Significant TB burden continues',
            'Malaria in some regions (declining from historical levels)',
            'Malnutrition and micronutrient deficiencies (rural and conflict-affected areas)',
            'Widespread food insecurity and humanitarian needs',
            'Pharma: Anti-infectives, antimalarials, TB support, ORS + zinc, paediatric antibiotics, dewormers',
            'Nutra: Iron & folate, blood builders, multivitamins, protein, appetite-enhancing formulations'
          ]
        },
        {
          name: 'Best-Fit Portfolio Buckets',
          items: [
            'Pharma: Anti-infectives, antimalarials, cardiometabolic drugs, paediatrics, pain/fever',
            'Nutra: Iron & folate, blood builders, immune tonics, metabolic support, liver support, paediatric multivitamins'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Food and Drug Administration (FDA Myanmar)',
      description: 'FDA Myanmar under Ministry of Health, upgraded to separate department in 2013. Responsible for safety and quality of food, drugs, medical devices and cosmetics. Follows National Drug Law and 2014 Drug Registration Application Guideline.',
      pharma: {
        timeline: 'Varies based on dossier completeness and FDA evaluation',
        requirements: [
          'ASEAN Common Technical Dossier (ACTD) format required',
          'ACTD Parts: I (Administrative), II (Quality), III (Non-clinical), IV (Clinical)',
          'Application via local company or authorized representative using Form 1',
          'Separate registration for different strengths or dosage forms',
          'Manufacturer\'s GMP certificate and licences required',
          'Evidence of registration in country of origin (where applicable)',
          'Registration assessment fees payable to FDA',
          'Myanmar-language labelling required on consumer units',
          'FDA evaluation with query management process',
          'Marketing Authorization valid for defined period, subject to renewal'
        ],
        insight: 'Myanmar requires ASEAN CTD (ACTD) for drug registration. Once ACTD dossiers are built, they can be adapted to other ASEAN markets. Local applicant/representative required.'
      },
      nutra: {
        timeline: 'Depends on formulation, vitamin/mineral levels, and documentation',
        requirements: [
          'Food Control Division of FDA oversees OTC and food supplement registration',
          'Vitamin & mineral limits aligned with ASEAN (Food Category 1B)',
          'Maximum daily levels: Vit A 1.5mg, Vit D 0.025mg, Vit C 1000mg, Ca 1200mg (adults)',
          'Supplements exceeding limits may be reclassified as drugs',
          'Local entity as applicant (Myanmar resident/company) for imports',
          'Full formula (qualitative + quantitative) and COAs required',
          'Safety information for novel or high-dose ingredients',
          'Myanmar-language labels (plus English allowed) with directions, storage, warnings'
        ],
        insight: 'Clear vitamin/mineral limits and dedicated Food Control Division provide structured path for nutraceutical launches. Products must respect upper limits to remain classified as health supplements.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration with FDA Myanmar',
      steps: [
        'Local Applicant / Representative - Submit via Form 1 with local company or authorized rep',
        'Separate Applications - Required for different strengths or dosage forms',
        'Dossier Format ACTD - ASEAN CTD with Parts I-IV (Administrative, Quality, Non-clinical, Clinical)',
        'Assessment & Fees - Registration fees payable to FDA at submission',
        'GMP & Supporting Documentation - Manufacturer\'s GMP certificate, licences, country of origin registration',
        'Evaluation & Queries - FDA dossier review with deficiency management',
        'Marketing Authorization - Registration certificate issued, valid for defined period',
        'Renewal - Subject to periodic renewal requirements'
      ]
    },
    
    nutraPathway: {
      title: 'Health Supplements & Nutraceuticals in Myanmar',
      steps: [
        'Food Control Division oversight - FDA Food Control Division handles registration',
        'Vitamin & Mineral Compliance - Must respect ASEAN-aligned maximum daily levels',
        'Classification check - Ensure formulation stays within supplement limits',
        'Local applicant setup - Myanmar resident/company required for imported supplements',
        'Full formula preparation - Qualitative + quantitative with COAs',
        'Safety documentation - For novel or high-dose ingredients',
        'Microbiological testing - COAs and test results',
        'GMP certification - Manufacturing site quality certification',
        'Myanmar-language labelling - Directions, storage, warnings, side-effects',
        'FDA evaluation and approval - Food supplement registration issuance'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (ACTD)': [
        'Application Form 1 and administrative documents (local applicant, licences, fees)',
        'ACTD Parts I-IV (administrative, quality, non-clinical, clinical)',
        'Manufacturer\'s GMP certificate and licence',
        'Evidence of registration/authorization in country of origin',
        'Finished product specifications & Certificates of Analysis',
        'Stability data for hot, humid climate',
        'Pack inserts and labels with Myanmar language on consumer units'
      ],
      'Nutraceuticals / Food Supplements': [
        'Application via local Myanmar entity (resident)',
        'Full formula with vitamin/mineral levels within national/ASEAN maxima',
        'COAs and microbiological tests',
        'GMP / quality certification of manufacturing site',
        'Labelling artwork (Myanmar + English)',
        'Claims compliant with supplement status',
        'Safety data for novel ingredients'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Myanmar Should Be in Your Asia Expansion Plan',
      reasons: [
        'High import dependence: ~80% of pharmaceutical supplies imported',
        'Limited local manufacturing - opportunity for quality imports',
        'Growing NCD burden: Rising hypertension and diabetes',
        'Continued infectious disease and malnutrition challenges',
        'Strong Indian presence: Indian generics play major market role',
        'ACTD-based system: Dossiers adaptable to other ASEAN markets',
        'Clear supplement pathway: Vitamin/mineral limits and dedicated Food Control Division',
        'Price-sensitive market favoring affordable quality generics'
      ],
      bestPortfolio: [
        'Pharma: Anti-infectives, antimalarials, cardiometabolic drugs, paediatrics, pain/fever',
        'Nutra: Iron & folate, blood builders, immune tonics, metabolic support, liver support, paediatric multivitamins'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Register in Myanmar',
      services: [
        'Market & Portfolio Fit Study - Match products to Myanmar\'s dual burden (infection + NCD) and pricing',
        'ACTD Dossier Development - Build/adapt ASEAN CTD aligned with FDA Myanmar\'s 2014 guideline',
        'Nutraceutical Strategy - Position as food supplements respecting vitamin/mineral maxima',
        'Label & Language Compliance - Coordinate Myanmar-language pack texts with English artwork',
        'Local Partner Coordination - Support importer in MAH role and documentation flow',
        'Lifecycle & Variations - Renewals, formulation changes, supplement ingredient updates'
      ]
    },
    
    faqs: [
      {
        question: 'Which authority regulates medicines and supplements in Myanmar?',
        answer: 'The Food and Drug Administration (FDA Myanmar) under the Ministry of Health regulates drugs, medical devices, food, cosmetics, and supplements. The Food Control Division handles OTC and food supplement registration.'
      },
      {
        question: 'What dossier format is required for pharma products?',
        answer: 'Myanmar requires drug registration dossiers in ASEAN Common Technical Dossier (ACTD) format, covering administrative, quality, non-clinical and clinical data.'
      },
      {
        question: 'Are there specific limits for vitamins and minerals in supplements?',
        answer: 'Yes. Myanmar has maximum permitted levels for vitamins and minerals in health supplements (Food Category 1B) closely aligned with ASEAN limits. For example, adults: Vit A 1.5mg/day, Vit D 0.025mg/day, Vit C 1000mg/day, Calcium 1200mg/day.'
      },
      {
        question: 'Is Myanmar a good market for Indian generics?',
        answer: 'Yes. Myanmar is highly reliant on imported pharmaceuticals (~80%), with Indian companies already among the leading suppliers. India contributes USD 200-300M+ in medicine imports.'
      },
      {
        question: 'Do labels need to be in local language?',
        answer: 'Yes. Myanmar-language labels are required on consumer units, including directions, storage, warnings and side-effects. These can appear alongside English or other languages.'
      }
    ]
  };

  return <CountryTemplate countryData={myanmarData} />;
};
